import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { map } from 'lodash';
import { Chip } from '@mui/material';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { chipStyles, formatPercent, formatPrice, getFormattedDate } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { BasicDataGrid } from '../Table/BasicDataGrid';
import { DetailViewCard } from '../CardWithTitle';
import {
  fetchAttributions,
  getAttributionSubject,
  getAttributionLoadingSubject,
  Attribution
} from '../../../store/Attribution';

const financialColumns = {
  chargeRecurringExpected: 'Expected MRC',
  chargeRecurringActual: 'Actual MRC',
  chargeRecurringDifference: 'MRC Diff',
  revenueRecurringExpected: 'Expected MRR',
  revenueRecurringActual: 'Actual MRR',
  revenueRecurringDifference: 'MRR Diff',
  revenueRecurringPercentExpected: 'Expected %',
  revenueRecurringPercentActual: 'Actual %',
  chargeOnetimeExpected: 'Expected NRC',
  chargeOnetimeActual: 'Actual NRC',
  chargeOnetimeDifference: 'NRC Diff',
  revenueOnetimeExpected: 'Expected NRR',
  revenueOnetimeActual: 'Actual NRR',
  revenueOnetimeDifference: 'NRR Diff',
  revenueRecurringNetActual: 'Net MRR',
  revenueOneTimeNetActual: 'Net NRR',
  revenueTrueupExpected: 'Expected True-Up',
  revenueTrueupActual: 'Actual True-Up',
  revenueSpiffActual: 'SPIFF'
};

const gridCols: GridColDef[] = [
  {
    field: 'runDate',
    headerName: 'Run',
    headerAlign: 'left',
    align: 'left',
    flex: 0.75,
    minWidth: 75,
    type: 'singleSelect',
    sortable: false,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      const date = params.value as string;
      if (!date) return '';
      return <span className="font-bold">{getFormattedDate(date, 'MMM YY')}</span>;
    }
  },
  {
    field: 'runStatus',
    headerName: 'Status',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 130,
    sortable: false,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    }
  },
  ...map(
    Object.keys(financialColumns),
    (key) =>
      ({
        headerName: financialColumns[key as keyof typeof financialColumns],
        field: key,
        renderCell: (params: GridCellParams) => {
          const value = params.value as number | undefined;
          const isPercent = key.toLowerCase().includes('percent');
          const isDiff = key.toLowerCase().includes('difference');
          if (isDiff && value === null) return <span className="float-right text-price-red">(No Data)</span>;
          if (value === undefined || value === null) return '';
          if (isPercent) {
            return (
              <span className={`float-right ${Number(value) < 0 ? 'text-price-red' : ''}`}>
                {formatPercent(value, true)}
              </span>
            );
          }
          return (
            <span className={`float-right ${value && Number(value) < 0 ? 'text-price-red' : ''}`}>
              {formatPrice((value as number) || 0)}
            </span>
          );
        },
        flex: 1,
        minWidth: 120,
        filterable: false,
        sortable: false,
        headerAlign: 'right',
        headerClassName: key.includes('NetActual') ? '!bg-azure-200' : undefined,
        cellClassName: key.includes('NetActual') ? '!bg-azure-100' : undefined
      }) as GridColDef
  ),
  {
    field: 'paymentFrequency',
    headerName: 'Pay Freq',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    filterable: false,
    flex: 1,
    minWidth: 100,
    renderCell: (params: GridCellParams) => {
      const value = params.value as string;
      if (!value) return '';
      return (
        <Chip
          label={value}
          size="small"
          sx={chipStyles(value)}
          title={value}
        />
      );
    }
  },
  {
    field: 'allocationCount',
    headerName: 'Links',
    align: 'right',
    sortable: false,
    filterable: false,
    flex: 0.5,
    minWidth: 60
  },
  {
    renderCell: (params: GridCellParams) => (params.value ? 'Yes' : 'No'),
    field: 'openTicket',
    headerName: 'Open Ticket',
    sortable: false,
    filterable: false,
    flex: 0.75,
    minWidth: 90
  },
  {
    renderCell: (params: GridCellParams) => (params.value as string[]).join(', '),
    field: 'allocationMethods',
    headerName: 'Methods',
    sortable: false,
    filterable: false,
    flex: 1,
    minWidth: 150
  },
  {
    field: 'updatedAt',
    headerName: 'Last Calculated At',
    sortable: false,
    filterable: false,
    minWidth: 180,
    flex: 1,
    description: 'Drag to move column',
    valueFormatter: (value) => getFormattedDate(value, 'lll'),
    valueGetter: (value) => (value ? new Date(value) : '')
  }
];

interface AttributionTableProps {
  entityType: string;
  entityId: string;
}

export const AttributionTable = ({ entityType, entityId }: AttributionTableProps) => {
  const [records, setRecords] = useState<Attribution[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchAttributions(entityType, entityId);

    const attributionSub = getAttributionSubject().subscribe((data) => {
      if (data) {
        setRecords(data.attributions);
      }
    });

    const loadingSub = getAttributionLoadingSubject().subscribe((isLoading) => {
      setLoading(isLoading);
    });

    return () => {
      if (attributionSub) attributionSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, [entityType, entityId]);

  const hiddenCols = entityType === 'provisional-asset' ? ['revenueRecurringNetActual', 'revenueOneTimeNetActual'] : [];
  const filteredGridCols = hiddenCols.length > 0 ? gridCols.filter((col) => !hiddenCols.includes(col.field)) : gridCols;

  return (
    <DetailViewCard
      enableCollapse
      key="attribution-table-card"
      title="Attribution"
      dataTestId="attribution-view-card">
      <div
        className="w-full"
        data-testid="attribution-table-container">
        {loading && <ComponentSpinner label="Loading Data" />}
        {!loading && (
          <BasicDataGrid
            rows={records}
            columns={filteredGridCols}
            sxStyle={DEFAULT_MINI_TABLE_SX_STYLES}
            disableColumnMenu
            showColumnVerticalBorder
            showCellVerticalBorder
            primaryKey="tallyRunId"
            dataTestId="attribution-table"
            pinnedColumns={{ left: ['runDate', 'runStatus'], right: [] }}
          />
        )}
      </div>
    </DetailViewCard>
  );
};
